import { createRootRouteWithContext, createRoute } from "@tanstack/react-router";
import type { RouterContext } from "@empite/app-kit";
import { Layout } from "./screens/layout";
import { Home } from "./screens/home";
import { SignIn } from "./screens/sign-in";
import type { SignInSearch } from "./screens/sign-in";
import { SignUp } from "./screens/sign-up";
import { AcceptInvite } from "./screens/accept-invite";
import type { AcceptInviteSearch } from "./screens/accept-invite";
import { Team } from "./screens/team";
import { APIKeys } from "./screens/api-keys";
import { Applications } from "./screens/applications";
import { ApplicationDetail } from "./screens/application-detail";
import { ApplicationCreate } from "./screens/application-form";
import { Users } from "./screens/users";
import type { UsersSearch } from "./screens/users";
import { UserDetail } from "./screens/user-detail";
import { Providers } from "./screens/providers";
import { ProviderTemplates } from "./screens/provider-templates";
import { ProviderDetail } from "./screens/provider-detail";
import { Models } from "./screens/models";
import { Tiers } from "./screens/tiers";
import { Tenants } from "./screens/tenants";
import { TenantDetail } from "./screens/tenant-detail";
import { Billing } from "./screens/billing";
import {
  CheckoutCancelled,
  CheckoutSuccess,
  type CheckoutReturnSearch,
} from "./screens/billing-return";
import { Plans } from "./screens/plans";
import { Analytics } from "./screens/analytics";
import { Revenue } from "./screens/revenue";
import type { AnalyticsSearch } from "./screens/analytics";
import { Drift } from "./screens/drift";
import type { DriftSearch } from "./screens/drift";
import { RoutingProfiles } from "./screens/routing-profiles";
import { RoutingProfileDetail } from "./screens/routing-profile-detail";
import { PriceProposals } from "./screens/price-proposals";
import { Pricing } from "./screens/pricing";
import { Packages } from "./screens/packages";
import type { PackagesSearch } from "./screens/packages";
import { PackageCreate, PackageDetail } from "./screens/package-detail";
import type { PackageCreateSearch } from "./screens/package-detail";
import { Wallets } from "./screens/wallets";
import type { WalletsSearch } from "./screens/wallets";
import { WalletDetail } from "./screens/wallet-detail";
import { Payments } from "./screens/payments";
import type { PaymentsSearch } from "./screens/payments";
import { Alerts } from "./screens/alerts";
import type { AlertsSearch } from "./screens/alerts";
import { Audit } from "./screens/audit";
import { Failures } from "./screens/failures";
import type { FailuresSearch } from "./screens/failure-log";
import { Guide } from "./screens/guide";
import type { AuditSearch } from "./screens/audit";

/**
 * The route table, defined in code rather than discovered from the file
 * system — same as the customer portal, and for the same reason: this is the
 * whole list of things the console can be pointed at, and it should be
 * readable in one screen rather than inferred from a directory.
 *
 * Every screen that reads the URL is handed its search parameters as a PROP,
 * parsed by the route's own validateSearch. Nothing downstream touches a raw
 * query string, so a hostile link cannot reach a component as an unexpected
 * type.
 */
function text(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * The one search parameter that is not a string.
 *
 * TanStack's default serialiser round-trips JSON literals, so `?developer=true`
 * arrives here as a real boolean — but the link may have been hand-edited, and
 * the state we mean is a tri-state: true, false, or not asked. Anything that is
 * not literally a boolean collapses to undefined, and undefined is "show both",
 * which is the answer that hides nothing.
 */
function flag(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

/**
 * A day count, on the same rule as flag above: a positive whole number or
 * nothing.
 *
 * NOT clamped. The Admin API's own ceiling is 90 days and it reports a moved
 * bound as `clamped` in the answer, which the analytics screen draws a banner
 * from — clamping here as well would silently satisfy an ask the operator
 * would otherwise be told was shortened. `?days=month` collapses to undefined
 * and the server applies its default; the window the answer states is what the
 * page is labelled with, so nothing ends up under a heading that lies.
 */
function count(value: unknown): number | undefined {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : undefined;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Layout });

const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: Home });

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    next: text(search.next),
  }),
  component: () => <SignIn search={signInRoute.useSearch()} />,
});

// The realm's only self-service way in, and the only route here that creates
// a TENANT. No search parameters: there is nothing about registering a company
// that another page can usefully prefill.
const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-up",
  component: SignUp,
});

/**
 * `/accept-invite?token=…` — the path internal/admin's deliverInvitation names
 * in the invitation email, so this string is a contract with the server and
 * not a routing preference. The token arrives in a URL like every other search
 * parameter and is validated the same way; an absent or non-string one leaves
 * the field empty and the screen asks for it.
 */
const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-invite",
  validateSearch: (search: Record<string, unknown>): AcceptInviteSearch => ({
    token: text(search.token),
  }),
  component: () => <AcceptInvite search={acceptInviteRoute.useSearch()} />,
});

const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team",
  component: Team,
});

const apiKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/api-keys",
  component: APIKeys,
});

const applicationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications",
  component: Applications,
});

// Ranked above the dynamic route below it whatever the order here, for the
// reason packageCreateRoute gives: TanStack ranks a static segment above a
// parameter, so an application id can never shadow this.
const applicationCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/new",
  component: ApplicationCreate,
});

/**
 * The window the "Usage and revenue" card on this page answers over, parsed
 * exactly as `/analytics` and `/users/$userId` parse theirs — same three
 * parameters, same refusal to clamp here, same reason.
 *
 * The rest of the page ignores it. A stale or hand-edited window cannot stop
 * the application, its OAuth clients or its entitlements from rendering,
 * because none of them is asked about a window at all.
 */
const applicationDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/$applicationId",
  validateSearch: (search: Record<string, unknown>): AnalyticsSearch => ({
    since: text(search.since),
    until: text(search.until),
    days: count(search.days),
  }),
  component: () => (
    <ApplicationDetail
      applicationId={applicationDetailRoute.useParams().applicationId}
      search={applicationDetailRoute.useSearch()}
    />
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/users",
  // The search lives in the URL so a result set can be linked into a ticket.
  // It is still validated: it arrives from whoever wrote the link.
  validateSearch: (search: Record<string, unknown>): UsersSearch => ({
    q: text(search.q),
    status: text(search.status),
    developer: flag(search.developer),
  }),
  component: () => <Users search={usersRoute.useSearch()} />,
});

/**
 * The window the Usage card on this page answers over, parsed exactly as
 * `/analytics` parses its own — same three parameters, same refusal to clamp
 * here, same reason. It is on THIS route rather than on the card's own state
 * because the reason an operator narrows the window is usually to paste the
 * result into a ticket, and a span held in a component cannot be linked to.
 *
 * The rest of the page ignores it. A stale or hand-edited window cannot stop
 * the account, the devices or the sessions from rendering, because none of
 * them is asked about a window at all.
 */
const userDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/users/$userId",
  validateSearch: (search: Record<string, unknown>): AnalyticsSearch => ({
    since: text(search.since),
    until: text(search.until),
    days: count(search.days),
  }),
  component: () => (
    <UserDetail
      userId={userDetailRoute.useParams().userId}
      search={userDetailRoute.useSearch()}
    />
  ),
});

const providersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/providers",
  component: Providers,
});

const providerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/providers/$accountId",
  component: () => <ProviderDetail accountId={providerDetailRoute.useParams().accountId} />,
});

/**
 * The platform's provider catalogue, on its own path.
 *
 * Separate from /providers because they are two different things owned by two
 * different audiences: a template is the platform's definition of a provider,
 * an account is one tenant's key for it. The screen refuses itself to a tenant
 * operator, and the API refuses the writes underneath it twice over.
 */
const providerTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/provider-templates",
  component: ProviderTemplates,
});

/**
 * The platform's own two screens over `tenancy`: the tier price list, and the
 * roster of who is on which tier.
 *
 * Both are platform-only in the same way Templates above is — the permissions
 * behind them (`tenants:manage`, `tenants:read_all`) are refused to any
 * tenant-confined subject by AllowedInScope — and both refuse themselves to a
 * tenant operator with a sentence rather than letting the API answer with an
 * audited 403. They are separate routes because they are separate grants: a
 * seat trusted to see that a company is running out is not automatically a seat
 * trusted to re-price what it is running out of.
 */
const tiersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tiers",
  component: Tiers,
});

/**
 * The tenant's own two, and the mirror of the platform's pair above: those read
 * across every company, these read exactly one — the caller's, off the
 * session's scope, never off the URL. Neither takes a search parameter for that
 * reason: there is no company to name.
 */
const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing",
  component: Billing,
});

/**
 * Where a hosted tier checkout sends the browser back to. The Admin API builds
 * both URLs off ADMIN_PORTAL_URL (tenant_billing.go), never off the request.
 * Landing on success proves nothing — the page polls the overview until the
 * webhook has actually moved the tier.
 */
const checkoutSuccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing/checkout/success",
  validateSearch: (search: Record<string, unknown>): CheckoutReturnSearch => ({
    tier: text(search.tier),
  }),
  component: () => <CheckoutSuccess tier={checkoutSuccessRoute.useSearch().tier} />,
});

const checkoutCancelledRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing/checkout/cancelled",
  component: CheckoutCancelled,
});

const plansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/plans",
  component: Plans,
});

const tenantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tenants",
  component: Tenants,
});

const tenantDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tenants/$tenantId",
  component: () => <TenantDetail tenantId={tenantDetailRoute.useParams().tenantId} />,
});

const modelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/models",
  component: Models,
});

/**
 * The window lives in the URL because a dashboard reading is a thing an
 * operator pastes into an incident write-up, and because a refresh on a wall
 * monitor must not silently reset the span back to the default.
 *
 * All three parameters are carried even though `since` + `until` + `days`
 * over-determines a window: the Admin API refuses that combination with a 400
 * rather than dropping one of the three, and dropping one here would be this
 * console making the choice the platform declines to make. The picker cannot
 * produce it; a hand-edited link that does gets the server's refusal, shown in
 * the screen's error state.
 */
const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics",
  validateSearch: (search: Record<string, unknown>): AnalyticsSearch => ({
    since: text(search.since),
    until: text(search.until),
    days: count(search.days),
  }),
  component: () => <Analytics search={analyticsRoute.useSearch()} />,
});

/**
 * `/analytics/revenue` — the same three window parameters, parsed the same
 * way, and a separate route rather than a tab on the dashboard: it answers a
 * different document from a different endpoint under a second permission, and
 * a URL an operator pastes into a finance thread should land on the money.
 *
 * Registered as its own top-level path alongside `/analytics`. TanStack ranks
 * a static segment above a parameter, so the two cannot shadow one another.
 */
const analyticsRevenueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics/revenue",
  validateSearch: (search: Record<string, unknown>): AnalyticsSearch => ({
    since: text(search.since),
    until: text(search.until),
    days: count(search.days),
  }),
  component: () => <Revenue search={analyticsRevenueRoute.useSearch()} />,
});

const routingProfilesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/routing-profiles",
  component: RoutingProfiles,
});

const routingProfileDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/routing-profiles/$profileId",
  component: () => (
    <RoutingProfileDetail profileId={routingProfileDetailRoute.useParams().profileId} />
  ),
});

// No search parameters on pricing: the two lists are read newest-first and
// the forms hold their own state. A price an operator is drafting has no
// business in a URL that gets pasted into a ticket.
const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pricing",
  component: Pricing,
});

// A static segment under /pricing rather than a tab on it: the review queue is
// where a price CHANGE is decided and the pricing page is where the history is
// read, and an operator sent a link to one should not land on the other.
// TanStack ranks a static segment above a parameter, so the order here is
// immaterial.
const priceProposalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pricing/proposals",
  component: PriceProposals,
});

// The application and status filters live in the URL for the same reason the
// users search does: "App B's archived packages" is the thing an operator
// pastes into a ticket, and the back button has to return to it rather than to
// the whole catalogue.
const packagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/packages",
  validateSearch: (search: Record<string, unknown>): PackagesSearch => ({
    application_id: text(search.application_id),
    status: text(search.status),
  }),
  component: () => <Packages search={packagesRoute.useSearch()} />,
});

// Before the dynamic route below it in the table, and matched before it
// whatever the order here: TanStack ranks a static segment above a parameter,
// so a package can never be called "new" hard enough to shadow this.
const packageCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/packages/new",
  // The application is a PREFILL carried from the catalogue's filter, not an
  // instruction: the form still asks for it, and the server still decides.
  validateSearch: (search: Record<string, unknown>): PackageCreateSearch => ({
    application_id: text(search.application_id),
  }),
  component: () => <PackageCreate search={packageCreateRoute.useSearch()} />,
});

const packageDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/packages/$packageId",
  component: () => <PackageDetail packageId={packageDetailRoute.useParams().packageId} />,
});

const walletsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wallets",
  validateSearch: (search: Record<string, unknown>): WalletsSearch => ({
    q: text(search.q),
    status: text(search.status),
  }),
  component: () => <Wallets search={walletsRoute.useSearch()} />,
});

const walletDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wallets/$walletId",
  component: () => <WalletDetail walletId={walletDetailRoute.useParams().walletId} />,
});

const paymentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/payments",
  validateSearch: (search: Record<string, unknown>): PaymentsSearch => ({
    status: text(search.status),
  }),
  component: () => <Payments search={paymentsRoute.useSearch()} />,
});

// The audit filter lives in the URL because a filtered trail is the thing an
// operator pastes into an incident write-up, and because the CSV export is
// built from the same filter — a link that reproduces the page reproduces the
// file.
const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit",
  validateSearch: (search: Record<string, unknown>): AuditSearch => ({
    outcome: text(search.outcome),
    resource_type: text(search.resource_type),
    action: text(search.action),
    since: text(search.since),
    until: text(search.until),
  }),
  component: () => <Audit search={auditRoute.useSearch()} />,
});

/**
 * The failure log's filter lives in the URL for the reason the audit filter's
 * does, plus one this screen has on its own: two of the nine parameters —
 * `user` and `device` — have no control in the form and arrive only by being
 * followed out of a row. If they lived in component state, "every fault on this
 * installation" would be a view an operator could reach and could not send to
 * anybody.
 *
 * Nothing is validated beyond "is it a non-empty string". The four vocabulary
 * parameters are CLOSED sets and the Admin API refuses a value outside one with
 * a 400 naming the accepted set — narrowing them here would answer the
 * unfiltered question under a heading claiming a filter, which on this screen
 * is the console reporting a service as healthy. The refusal lands in the
 * screen's error state, where it says what was wrong with it.
 */
const failuresRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/failures",
  validateSearch: (search: Record<string, unknown>): FailuresSearch => ({
    service: text(search.service),
    kind: text(search.kind),
    severity: text(search.severity),
    acknowledged: text(search.acknowledged),
    since: text(search.since),
    until: text(search.until),
    user: text(search.user),
    device: text(search.device),
    application: text(search.application),
  }),
  component: () => <Failures search={failuresRoute.useSearch()} />,
});

/**
 * `/alerts` — what the platform has NOTICED, beside `/failures`, which is what
 * it got wrong.
 *
 * The filter lives in the URL for the reason the failure filter's does: "every
 * critical runout still open" is the view an operator pastes into a ticket,
 * and the back button has to return to it rather than to the whole list.
 *
 * Nothing is validated beyond "is it a non-empty string". `kind`, `severity`
 * and `state` are CLOSED sets and the Admin API refuses a value outside one
 * with a 400 naming what it accepts — narrowing them here would answer the
 * unfiltered question under a heading claiming a filter, which on this screen
 * means telling an operator that nobody is running out of money. The refusal
 * lands in the screen's error state, where it says what was wrong with it.
 */
const alertsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/alerts",
  validateSearch: (search: Record<string, unknown>): AlertsSearch => ({
    kind: text(search.kind),
    severity: text(search.severity),
    state: text(search.state),
  }),
  component: () => <Alerts search={alertsRoute.useSearch()} />,
});

/**
 * `/analytics/drift` — where the PROVIDER's accounting disagrees with ours.
 *
 * The filter lives in the URL for the reason the alert filter's does: "every
 * bucket of this model that drifted last week" is the view an operator pastes
 * into a ticket, and the back button has to return to it.
 *
 * Nothing is validated beyond "is it a non-empty string", including the two
 * dates. The Admin API refuses an unreadable one with a 400 naming the form it
 * wants; narrowing them here would drop the parameter and answer the UNFILTERED
 * question under a heading claiming a range, which on this screen means
 * reporting agreement over days nobody asked about.
 */
const driftRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/analytics/drift",
  validateSearch: (search: Record<string, unknown>): DriftSearch => ({
    from: text(search.from),
    to: text(search.to),
    provider: text(search.provider),
    drifted: text(search.drifted),
  }),
  component: () => <Drift search={driftRoute.useSearch()} />,
});

const guideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/guide",
  component: Guide,
});

export const routeTree = rootRoute.addChildren([
  homeRoute,
  signInRoute,
  signUpRoute,
  acceptInviteRoute,
  teamRoute,
  apiKeysRoute,
  applicationsRoute,
  applicationCreateRoute,
  applicationDetailRoute,
  usersRoute,
  userDetailRoute,
  providersRoute,
  providerTemplatesRoute,
  providerDetailRoute,
  modelsRoute,
  tiersRoute,
  tenantsRoute,
  tenantDetailRoute,
  billingRoute,
  checkoutSuccessRoute,
  checkoutCancelledRoute,
  plansRoute,
  analyticsRoute,
  analyticsRevenueRoute,
  driftRoute,
  routingProfilesRoute,
  routingProfileDetailRoute,
  pricingRoute,
  priceProposalsRoute,
  packagesRoute,
  packageCreateRoute,
  packageDetailRoute,
  walletsRoute,
  walletDetailRoute,
  paymentsRoute,
  auditRoute,
  failuresRoute,
  alertsRoute,
  guideRoute,
]);
